/**
 * Epoch content keys + registry seam (F6, ADR-12 §keyRef / §registry API).
 * The interface is what A2's Rust registry will serve over HTTP; the in-memory
 * impl is the pilot stand-in (mock register: software keystore — interface
 * real, custody mocked, SHRED REAL). There is deliberately no shred() on the
 * KeyRegistry interface: erasure is event-folded (privacy.shred), never a
 * client-callable API — InMemoryKeyRegistry.shred exists for the fold/sim/tests.
 */
import { makeKeyRef } from "@beacon/crypto";

export interface KeyRegistry {
  /**
   * Upload own epoch key before first use (identity-signed in the real impl).
   * First-write-wins is contractual: if a key already exists for keyRef, put is
   * a no-op (A2's Rust registry must replicate this — it is half of the
   * epoch-key race semantics; the other half is callers re-fetching after put
   * and adopting the registry's canonical key).
   */
  put(keyRef: string, key32: Uint8Array): Promise<void>;
  /** Resolve at delivery (Privacy §4.1). null = unknown or shredded. */
  get(keyRef: string): Promise<Uint8Array | null>;
  /**
   * Inject the signer that builds the signed PUT body (HTTP impl only).
   * Optional: InMemoryKeyRegistry has no signer and does not implement it.
   * BeaconClient.create calls it with a closure over its live (rotation-safe) keys.
   */
  bindUploader?(upload: (keyRef: string, key32: Uint8Array) => Promise<Uint8Array>): void;
  /**
   * Inject a self-heal hook the HTTP impl runs when the registry rejects an
   * upload with `unknown-identity` (the relay was wiped but we kept our bundle):
   * re-emit genesis to the relay so the retried PUT resolves. Optional — only the
   * HTTP impl needs it; InMemoryKeyRegistry never rejects on identity.
   */
  bindHealer?(heal: () => Promise<void>): void;
}

export class InMemoryKeyRegistry implements KeyRegistry {
  private keys = new Map<string, Uint8Array>();
  async put(keyRef: string, key32: Uint8Array): Promise<void> {
    if (key32.length !== 32) throw new Error("registry: key must be 32 bytes");
    if (!this.keys.has(keyRef)) this.keys.set(keyRef, key32.slice());
  }
  async get(keyRef: string): Promise<Uint8Array | null> {
    const k = this.keys.get(keyRef);
    return k ? k.slice() : null;
  }
  /** Fold target for privacy.shred — destroys the key material itself. */
  shred(keyRef: string): void {
    const k = this.keys.get(keyRef);
    if (k) k.fill(0);
    this.keys.delete(keyRef);
  }
}

/** Per-identity epoch-key client: derive keyRef locally, generate + upload lazily. */
export class EpochKeys {
  private cache = new Map<string, Uint8Array>();
  private inflight = new Map<string, Promise<Uint8Array>>();
  constructor(
    private readonly did: string,
    private readonly registry: KeyRegistry,
  ) {}

  async current(nowMicros: number): Promise<{ keyRef: string; key: Uint8Array }> {
    const keyRef = makeKeyRef(this.did, nowMicros);
    const cached = this.cache.get(keyRef);
    if (cached) return { keyRef, key: cached };
    // Memoize the in-flight resolution so concurrent first uses of an epoch
    // share one generate/upload/adopt instead of racing each other.
    let pending = this.inflight.get(keyRef);
    if (!pending) {
      pending = this.resolve(keyRef).finally(() => this.inflight.delete(keyRef));
      this.inflight.set(keyRef, pending);
    }
    return { keyRef, key: await pending };
  }

  private async resolve(keyRef: string): Promise<Uint8Array> {
    // restart on the same device/epoch: re-adopt the uploaded key
    let key = await this.registry.get(keyRef);
    if (!key) {
      await this.registry.put(keyRef, crypto.getRandomValues(new Uint8Array(32))); // upload BEFORE first use (ADR-12)
      // put is first-write-wins: another instance may have won the race, so the
      // registry's key — not our candidate — is canonical. Adopt and cache THAT,
      // or everything we seal this epoch is unopenable via the registry.
      key = await this.registry.get(keyRef);
      if (!key) throw new Error("epoch-keys: registry has no key immediately after put");
    }
    this.cache.set(keyRef, key);
    return key;
  }
}
